import localforage from 'localforage';
import debug from 'debug';
import { getSessionStorageUser } from '../utils/sessionStorage';

const log = debug('rois:gql:cache-seed');

/**
 * Semilla de arranque en frío para la caché de Apollo.
 *
 * ── El problema ───────────────────────────────────────────────────────────
 * La caché persistida vive en `sessionStorage`, que es POR PESTAÑA. Abrir la
 * app en una pestaña nueva la encuentra vacía y paga el waterfall entero: entre
 * 10 y 15 segundos de pantalla en blanco antes de ver nada.
 *
 * ── Por qué NO se mueve la caché a IndexedDB ──────────────────────────────
 * Porque el aislamiento por pestaña que da `sessionStorage` es comportamiento
 * EN USO: el cliente trabaja con varias pestañas a la vez y cada una debe
 * mantener lo suyo. `apollo3-cache-persist` no sincroniza pestañas —no expone
 * ninguna primitiva para ello—, así que mover la persistencia a un almacén
 * compartido haría que dos pestañas se pisaran y ganara la última en escribir.
 *
 * ── La forma que respeta las dos cosas ────────────────────────────────────
 * En vez de MOVER la persistencia, se AÑADE una capa:
 *
 *   caché viva  → `sessionStorage`, igual que hoy. Es la que sirve a la pestaña
 *                 mientras trabaja. Nadie más la toca.
 *   semilla     → IndexedDB. SOLO se lee cuando `sessionStorage` está vacío.
 *
 * Con esto la carrera entre pestañas sigue existiendo pero se vuelve
 * **inofensiva**: solo afecta a una semilla que nadie está usando mientras
 * trabaja. Y es reversible — se quitan las llamadas y el comportamiento vuelve
 * a ser exactamente el de hoy.
 *
 * La semilla es una copia LITERAL de la cadena que el `CachePersistor` deja en
 * `sessionStorage`, no una serialización propia de la caché. Eso evita duplicar
 * lógica y hereda gratis el recorte del `maxSize`: lo que se copia ya pasó ese
 * filtro.
 *
 * ── Seguridad: por qué la clave lleva tenant y usuario ────────────────────
 * `sessionStorage` moría con la pestaña, así que los datos de un operario nunca
 * podían llegarle al siguiente. La semilla SÍ es duradera, así que introduce ese
 * riesgo — y las tablets de campo se comparten. Por eso el almacén se indexa por
 * `tenantId::userId`: el cruce es imposible por construcción, no por acordarse
 * de limpiar. Además se purga en el cierre de sesión.
 *
 * Está reportado en la librería (issue #220) que los persistors se acumulan
 * entre logins y los datos de un usuario acaban en la caché del otro; el
 * reportante solo lo resolvió reiniciando la app entera. No hay mecanismo limpio
 * para soltarlos, así que la defensa real es la clave.
 */

/** Clave que usa `CachePersistor` en `sessionStorage` (default de la librería). */
const APOLLO_SNAPSHOT_KEY = 'apollo-cache-persist';

/** Almacén propio: no se mezcla con la cola offline (`localforage` por defecto). */
const seedStore = localforage.createInstance({
  name: 'rois-apollo-seed',
  storeName: 'seeds',
  description: 'Semilla de arranque en frío de la caché de Apollo, por tenant y usuario',
});

/** Cada cuánto se refresca la semilla desde la caché viva. */
const INTERVALO_ESCRITURA_MS = 20_000;

/**
 * Identidad del usuario en sesión, o `null` si no hay.
 *
 * Sin identidad NO se lee ni se escribe semilla alguna: preferimos un arranque
 * frío antes que arriesgar servirle a alguien la caché de otro.
 */
export function getSeedKey(): string | null {
  try {
    const sesion = getSessionStorageUser();
    const userId: string | undefined = sesion?.user?.id;
    // `tenantId` está deprecado en favor de `activeTenantId`, pero se conserva
    // como respaldo para sesiones guardadas por versiones anteriores.
    const tenantId: string | undefined =
      sesion?.user?.activeTenantId ?? sesion?.user?.tenantId;
    if (!userId || !tenantId) return null;
    return `${tenantId}::${userId}`;
  } catch {
    return null;
  }
}

/**
 * Si `sessionStorage` no tiene snapshot y sí hay semilla para ESTA identidad,
 * la copia a `sessionStorage` para que el `CachePersistor` la restaure por su
 * cauce normal.
 *
 * No toca la caché en memoria ni se salta al persistor: solo deja el snapshot
 * donde la librería ya sabe buscarlo. Por eso no cambia la forma del arranque.
 *
 * @returns `true` si sembró algo.
 */
export async function hydrateSessionFromSeed(
  isAbandoned: () => boolean = () => false
): Promise<boolean> {
  try {
    const yaHay = window.sessionStorage.getItem(APOLLO_SNAPSHOT_KEY);
    if (yaHay) {
      log('sessionStorage ya tiene snapshot — la semilla no se toca');
      return false;
    }

    const key = getSeedKey();
    if (!key) {
      log('sin identidad en sesión — no se siembra (arranque frío a propósito)');
      return false;
    }

    const semilla = await seedStore.getItem<string>(key);
    // 🔴 Llegar tarde = la app ya arrancó con la caché vacía. Copiar ahora la
    // semilla no ayuda —el persistor ya restauró— y puede hacer daño: la clave
    // se calculó con el operario de ENTONCES, y si entre medias ha entrado otro,
    // su próxima recarga restauraría la caché del anterior.
    if (isAbandoned()) {
      log('seed arrived late — discarded');
      return false;
    }
    if (!semilla) {
      log('no hay semilla guardada para %s', key);
      return false;
    }

    window.sessionStorage.setItem(APOLLO_SNAPSHOT_KEY, semilla);
    log('sembrado %d kB desde IndexedDB para %s', Math.round(semilla.length / 1024), key);
    return true;
  } catch (error) {
    // Una semilla ilegible no puede impedir que la app arranque: se ignora y
    // se sigue en frío, que es exactamente el comportamiento de antes.
    log('error al sembrar: %o', error);
    return false;
  }
}

/** Copia el snapshot vivo de `sessionStorage` a la semilla. */
export async function writeSeedNow(): Promise<boolean> {
  try {
    const key = getSeedKey();
    if (!key) return false;
    const snapshot = window.sessionStorage.getItem(APOLLO_SNAPSHOT_KEY);
    if (!snapshot) return false;
    await seedStore.setItem(key, snapshot);
    log('semilla actualizada (%d kB) para %s', Math.round(snapshot.length / 1024), key);
    return true;
  } catch (error) {
    log('error al escribir la semilla: %o', error);
    return false;
  }
}

let intervalo: ReturnType<typeof setInterval> | null = null;
let onHidden: (() => void) | null = null;
let primeraEscritura: ReturnType<typeof setTimeout> | null = null;

/**
 * Margen antes de la primera escritura: lo justo para que el arranque haya
 * traído algo que valga la pena guardar, sin competir con él.
 */
const PRIMERA_ESCRITURA_MS = 4_000;

/**
 * Arranca el refresco periódico de la semilla.
 *
 * El intervalo es la vía principal; el `visibilitychange` es un mejor-esfuerzo
 * para capturar el estado justo antes de que la pestaña se vaya al fondo. NO se
 * usa `beforeunload`: una escritura a IndexedDB durante el cierre no tiene
 * garantía de completarse, así que confiar en ella daría una falsa sensación de
 * seguridad.
 *
 * ⚠️ La primera escritura NO espera al primer tick. Medido: sin esto la semilla
 * no existía hasta los ~20 s, así que quien cerrara la app antes se quedaba sin
 * ella — y es justo el arranque, cuando el operario aún no ha hecho nada que
 * merezca guardarse pero ya tiene cargados los catálogos que tanto cuestan.
 *
 * El intervalo es idempotente (no se duplica), pero la primera escritura se
 * REPROGRAMA en cada llamada a propósito: tras el login se vuelve a invocar
 * esta función, y es entonces —con identidad ya conocida— cuando de verdad se
 * puede escribir. Sin reprogramar, esa llamada no haría nada y habría que
 * esperar al tick.
 */
export function startSeedWriter(): void {
  if (primeraEscritura) clearTimeout(primeraEscritura);
  primeraEscritura = setTimeout(() => {
    primeraEscritura = null;
    void writeSeedNow();
  }, PRIMERA_ESCRITURA_MS);

  if (intervalo) return;

  intervalo = setInterval(() => {
    void writeSeedNow();
  }, INTERVALO_ESCRITURA_MS);

  onHidden = () => {
    if (document.visibilityState === 'hidden') void writeSeedNow();
  };
  document.addEventListener('visibilitychange', onHidden);
  log('escritor de semilla arrancado (cada %d s)', INTERVALO_ESCRITURA_MS / 1000);
}

export function stopSeedWriter(): void {
  if (primeraEscritura) {
    clearTimeout(primeraEscritura);
    primeraEscritura = null;
  }
  if (intervalo) {
    clearInterval(intervalo);
    intervalo = null;
  }
  if (onHidden) {
    document.removeEventListener('visibilitychange', onHidden);
    onHidden = null;
  }
  log('escritor de semilla detenido');
}

/**
 * Borra la semilla del usuario que cierra sesión.
 *
 * DEBE llamarse ANTES de vaciar `sessionStorage`: la clave se deriva de la
 * sesión, así que después ya no se sabría cuál borrar.
 */
export async function purgeSeedForCurrentUser(): Promise<void> {
  try {
    stopSeedWriter();
    const key = getSeedKey();
    if (!key) {
      log('cierre de sesión sin identidad legible — nada que purgar');
      return;
    }
    await seedStore.removeItem(key);
    log('semilla purgada para %s', key);
  } catch (error) {
    log('error al purgar la semilla: %o', error);
  }
}

/** Borra TODAS las semillas. Solo para diagnóstico y limpiezas de emergencia. */
export async function purgeAllSeeds(): Promise<void> {
  try {
    stopSeedWriter();
    await seedStore.clear();
    log('todas las semillas purgadas');
  } catch (error) {
    log('error al purgar todas las semillas: %o', error);
  }
}

/** Claves guardadas. Solo para tests y diagnóstico desde la consola. */
export async function listSeedKeys(): Promise<string[]> {
  try {
    return await seedStore.keys();
  } catch {
    return [];
  }
}
